import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, CircleHelp, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { setupChecklistQuery } from '@/api/setup';
import { useActiveSite } from '@/hooks/useActiveSite';
import { firstOpenWizardStep, setupWizardPath } from '@/lib/setupWizardRoute';

/**
 * "Finish setting up" — the dashboard's view of the setup checklist (GET /setup/checklist, built
 * by the gateway's lib/setup-checklist.ts, the same list BLT reads).
 *
 * The button opens the setup wizard at the first step that still needs doing, and each item links
 * to its wizard step (brand, look, MLS, legal) or its own page. It used to start a separate setup
 * agent in the chat, which skipped the brand and the look. Hidden once everything that isn't
 * optional is done.
 */
/**
 * The setup checklist state for the active site: one query, shared by this card and the BLT panel's
 * review (components/blt/BltSetupChecklist), so both show the same list from the same read.
 * The plan is in the key so an upgrade refreshes it (the gateway reads the plan itself).
 */
export function useSetupChecklistState() {
  const plan = useAuthStore((s) => s.plan);
  const { activeSiteId, ready } = useActiveSite();
  const { data } = useQuery({
    ...setupChecklistQuery(activeSiteId, plan),
    // Not before the active site is adopted: a read without X-Site-Id answers for the primary and
    // is then thrown away for the adopted site's.
    enabled: ready,
  });
  return { data, activeSiteId };
}

export function SetupChecklistCard() {
  const navigate = useNavigate();
  const { data, activeSiteId } = useSetupChecklistState();
  if (!data) return null;
  const items = data.items.filter((i) => i.id !== 'publish');
  const open = items.filter((i) => !i.done && !i.optional);
  if (!open.length) return null;
  const done = items.filter((i) => i.done).length;

  return (
    <section data-testid="setup-checklist-card" data-tour="dash-setup" className="rounded-xl border border-brand-border bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-semibold text-brand-midnight">Finish setting up</h2>
          <p className="mt-1 text-sm text-brand-ink-soft">
            {done} of {items.length} done. The setup wizard takes you through what&rsquo;s left, and its AI can
            write your bio, community pages and first blog posts.
          </p>
        </div>
        <Button
          size="sm"
          className="shrink-0 bg-brand-plum text-white hover:bg-brand-plum-deep"
          onClick={() => navigate(setupWizardPath({ step: firstOpenWizardStep(data.state), siteId: activeSiteId }))}
        >
          <Sparkles className="mr-1.5 h-4 w-4" />
          Continue setup
        </Button>
      </div>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => navigate(item.href)}
              className="flex w-full items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-brand-surface"
            >
              {item.done ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" aria-label="Done" />
              ) : item.unchecked ? (
                <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink-soft" aria-label="Couldn't check" />
              ) : (
                <Circle className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink-soft" aria-label="To do" />
              )}
              <span className="min-w-0">
                <span className={`block text-sm font-medium ${item.done ? 'text-brand-ink-soft line-through' : 'text-brand-midnight'}`}>
                  {item.label}
                  {item.optional && !item.done ? ' (optional)' : ''}
                </span>
                {!item.done ? <span className="block text-xs text-brand-ink-soft">{item.detail}</span> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
